import DiscountModel from "../models/DiscountModel";
import MeasurementModel from "../models/MeasurementModel";
import DiscountService from "../services/DiscountService";
import TransactionService from "../services/TransactionService";
import MeasurementController from "./MeasurementController";
import { RequiredParamException } from "../exceptions/InvalidParamException";
import { IDiscountLink, IInstallmentsLink } from "../interfaces";
import { isEmptyObject } from "../../utils";
import InstallmentsLinkModel from "../models/InstallmentsLinkModel";

class DiscountController {
  createDiscount(
    contractUid: string,
    { code, title, description, date, type, installments }
  ) {
    const discount = DiscountModel.create("", {
      contractUid,
      code,
      title,
      description,
      date,
      type,
      installments,
    });

    return DiscountService.createDiscount(discount);
  }

  updateDiscount(discount: DiscountModel, updatedData) {
    const updatedDiscount = DiscountModel.create(discount.uid, {
      contractUid: discount.contractUid,
      code: discount.code,
      date: updatedData.date,
      title: updatedData.title,
      description: updatedData.description,
      type: updatedData.type,
      installments: updatedData.installments,
    });

    return DiscountService.updateDiscount(discount, updatedDiscount);
  }

  async linkDiscountsToMeasurement(
    measurement: MeasurementModel,
    discountsLinks: IInstallmentsLink<DiscountModel>[]
  ): Promise<void> {
    if (!discountsLinks || !measurement) return;

    const measurementUid = measurement.uid;
    discountsLinks = discountsLinks.filter(
      (link) =>
        (link.target as DiscountModel).contractUid === measurement.contractUid
    );

    if (isEmptyObject(discountsLinks))
      throw new RequiredParamException("discountLinks");

    const datas = this.convertLinksToMeasurementLinks(discountsLinks);

    return TransactionService.run(async (transaction) => {
      await MeasurementController.linkDiscountsToMeasurement(
        transaction,
        measurement,
        datas.discountsLinks,
        datas.billingsLinks
      );

      for (const discountLink of discountsLinks)
        await this.addLinkToDiscount(
          transaction,
          discountLink.target,
          discountLink.installments,
          measurementUid
        );
    });
  }

  private convertLinksToMeasurementLinks(
    links: IInstallmentsLink<DiscountModel>[]
  ): { discountsLinks: IDiscountLink[]; billingsLinks: IDiscountLink[] } {
    const discountsLinks: IDiscountLink[] = [];
    const billingsLinks: IDiscountLink[] = [];

    InstallmentsLinkModel.expandList(links).forEach((link) => {
      const discount = link.target as DiscountModel;
      const data = {
        discountId: discount.uid,
        installment: link.installment,
        value: link.value,
      };

      if (discount.type === "faturamento") billingsLinks.push(data);
      else discountsLinks.push(data);
    });

    return { discountsLinks, billingsLinks };
  }

  private async addLinkToDiscount(
    transaction,
    discount: DiscountModel,
    installments: number[],
    measurementUid: string
  ) {
    discount.removeInstallmentsMeasurement(measurementUid);
    discount.setInstallmentsMeasurement(installments, measurementUid);

    await DiscountService.updateInstallmentsWithTransaction(
      transaction,
      discount
    );
  }
}

export default new DiscountController();
